/**
 * Awaab's Law widget (dashboard card).
 *
 * Portfolio-wide hazard-case compliance snapshot — same data the
 * Awaab's Law tab's summary shows, condensed into a card. Self-fetching,
 * same widget-card pattern as every other Dashboard component.
 */
import React, { useEffect, useState } from 'react';
import { awaabService } from '../../../services/api';

export default function AwaabLawWidget() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    closed: data?.portfolio?.totalClosedCases,
                    hhsrsCat1: data?.hhsrsCat1Open,
                    highRisk: data?.highRiskOpen,
                });
            })
            .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || "Failed to load Awaab's Law data"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-auto h-full">
            <div className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-house-chimney-crack text-orange-500 mr-2"></i>Awaab's Law</h2>
                {dashboard && (
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${dashboard.hhsrsCat1 > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {dashboard.hhsrsCat1 > 0 ? `${dashboard.hhsrsCat1} Cat 1` : 'No Cat 1'}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="text-center py-8 text-sm text-gray-400">Loading Awaab's Law data…</div>
            ) : error ? (
                <div className="text-center py-8 text-sm text-red-500">{error}</div>
            ) : !dashboard ? (
                <div className="text-center py-8 text-sm text-gray-500">
                    <i className="fas fa-info-circle text-2xl text-gray-300 mb-2 block"></i>
                    No hazard cases yet.
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-gray-900">{dashboard.open ?? '—'}</div>
                        <div className="text-xs font-semibold text-gray-600 mt-1">Open cases</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-gray-900">{dashboard.closed ?? '—'}</div>
                        <div className="text-xs font-semibold text-gray-600 mt-1">Closed cases</div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-red-700">{dashboard.hhsrsCat1 ?? '—'}</div>
                        <div className="text-xs font-semibold text-red-600 mt-1">HHSRS Cat 1</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-amber-700">{dashboard.highRisk ?? '—'}</div>
                        <div className="text-xs font-semibold text-amber-600 mt-1">High risk</div>
                    </div>
                </div>
            )}
        </div>
    );
}
